import React, { useState, useMemo } from 'react';
import { X, Users, Search, Check, MessageCirclePlus } from 'lucide-react';
import type { GroupDetail } from '../../../../types/groupDetail';
import { useSendbirdStateContext } from '@sendbird/uikit-react';
import type { GroupChannelCreateParams } from '@sendbird/chat/groupChannel';
import { InitialsAvatar } from '../../../shared/InitialsAvatar';

interface CreateChannelModalProps {
  groupDetail: GroupDetail;
  subdomain: string;
  onClose: () => void;
  onChannelCreated: (channelUrl: string) => void;
}

export const CreateChannelModal: React.FC<CreateChannelModalProps> = ({
  groupDetail,
  subdomain,
  onClose,
  onChannelCreated,
}) => {
  const [channelName, setChannelName] = useState('Group Channel');
  const [includeAllMembers, setIncludeAllMembers] = useState(true);
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sdkContext = useSendbirdStateContext();
  const sdk = sdkContext?.stores?.sdkStore?.sdk;

  // Get all group members excluding the current user (who is automatically included)
  const currentUserId = sdk?.currentUser?.userId;
  const members = (groupDetail?.members || []).filter(
    member => member.id.toString() !== currentUserId
  );

  // Filter members based on search query
  const filteredMembers = useMemo(() => {
    if (!searchQuery) return members;
    
    const query = searchQuery.toLowerCase();
    return members.filter(member => 
      member.name?.toLowerCase().includes(query) ||
      member.id?.toString().includes(query)
    );
  }, [members, searchQuery]);

  // Toggle member selection
  const toggleMember = (memberId: string) => {
    const newSelected = new Set(selectedMemberIds);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMemberIds(newSelected);
  };

  // Select all filtered members
  const selectAll = () => {
    const newSelected = new Set(selectedMemberIds);
    filteredMembers.forEach(member => {
      newSelected.add(member.id.toString());
    });
    setSelectedMemberIds(newSelected);
  };

  // Deselect all filtered members
  const deselectAll = () => {
    const newSelected = new Set(selectedMemberIds);
    filteredMembers.forEach(member => {
      newSelected.delete(member.id.toString());
    });
    setSelectedMemberIds(newSelected);
  };

  // Handle channel creation
  const handleCreateChannel = async () => {
    if (!sdk) {
      setError('Sendbird SDK is not initialized');
      return;
    }

    if (!channelName.trim()) {
      setError('Please enter a channel name');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      // Determine which members to invite
      const invitedUserIds = includeAllMembers
        ? members.map(m => m.id.toString())
        : Array.from(selectedMemberIds);

      if (invitedUserIds.length === 0) {
        setError('Please select at least one member');
        setIsCreating(false);
        return;
      }

      // Create channel parameters
      const params: GroupChannelCreateParams = {
        name: channelName.trim(),
        invitedUserIds,
        operatorUserIds: [sdk.currentUser?.userId || ''],
        isDistinct: false,
        customType: `${subdomain}-${groupDetail.id}-custom`,
      };

      // Create the channel
      const channel = await sdk.groupChannel.createChannel(params);
      
      // Notify parent component
      onChannelCreated(channel.url);
      onClose();
    } catch (err) {
      console.error('Failed to create channel:', err);
      setError(err instanceof Error ? err.message : 'Failed to create channel');
      setIsCreating(false);
    }
  };

  // Get member display name
  const getMemberDisplayName = (member: any) => {
    return member.name || `User ${member.id}`;
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
              <MessageCirclePlus className="w-10 h-10 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Create New Channel</h2>
              <p className="text-sm text-gray-500">Start a conversation with group members</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            disabled={isCreating}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Channel Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Channel Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isCreating}
            />
          </div>

          {/* Member Selection Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Members</label>
            <div className="space-y-2">
              {/* All Members Option */}
              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  checked={includeAllMembers}
                  onChange={() => setIncludeAllMembers(true)}
                  className="flex-shrink-0"
                  disabled={isCreating}
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">All Group Members</div>
                </div>
              </label>

              {/* Select Specific Members Option */}
              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  checked={!includeAllMembers}
                  onChange={() => setIncludeAllMembers(false)}
                  className="flex-shrink-0"
                  disabled={isCreating}
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Select Specific Members</div>
                </div>
              </label>
            </div>
          </div>

          {/* Member Selection List */}
          {!includeAllMembers && (
            <div className="border border-gray-200 rounded-md overflow-hidden">
              {/* Search Bar */}
              <div className="p-3 bg-gray-50 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search members..."
                    className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    disabled={isCreating}
                  />
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={selectAll}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    disabled={isCreating}
                  >
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={deselectAll}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    disabled={isCreating}
                  >
                    Deselect All
                  </button>
                </div>
              </div>

              {/* Member List */}
              <div className="max-h-64 overflow-y-auto">
                {filteredMembers.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No members found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredMembers.map((member) => {
                      const memberId = member.id.toString();
                      const isSelected = selectedMemberIds.has(memberId);
                      
                      return (
                        <label
                          key={memberId}
                          className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                            isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleMember(memberId)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              disabled={isCreating}
                            />
                          </div>
                          
                          <InitialsAvatar
                            name={member.name}
                            avatarUrl={member.avatarUrl}
                            size={32}
                          />
                          
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {getMemberDisplayName(member)}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {member.role}
                            </div>
                          </div>

                          {isSelected && (
                            <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {includeAllMembers ? (
              <span>Creating channel with you and <strong>{members.length}</strong> other member{members.length !== 1 ? 's' : ''}</span>
            ) : (
              <span>Creating channel with you and <strong>{selectedMemberIds.size}</strong> other member{selectedMemberIds.size !== 1 ? 's' : ''}</span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-md transition-colors font-medium"
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              onClick={handleCreateChannel}
              disabled={isCreating || !channelName.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isCreating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Channel'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
